import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import {
  PlaygroundLocalMsg,
  usePlaygroundLocalMsg,
} from "@faire/prototype-playground/platform/localization";
import { Add } from "@faire/slate-icons/Add";
import { ArrowRight } from "@faire/slate-icons/ArrowRight";
import { Filter } from "@faire/slate-icons/Filter";
import { Button } from "@faire/slate/components/button";
import { FilterButton } from "@faire/slate/components/filter";
import { tsv } from "@faire/slate/lib/tsv";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import RetailerLayout from "../../../components/RetailerLayout";
import { CompassProvider } from "../../../contexts/CompassContext";
import type { FaireProductEntity } from "@faire/playground-entity-db/entity_types/product";
import { getEntityByToken } from "../../../data/get_entity";

import {
  BRAND_FRAMING_DIVIDERS,
  brandFramingForVariant,
  brandFramingVariantGroup,
} from "./brandFramingVariants";
import {
  BrandGroupedProducts,
  savedProductGridStyle,
} from "./BrandGroupedProducts";
import {
  CART_ACTION_PILL,
  cartActionForVariant,
  cartActionVariantGroup,
} from "./cartActionVariants";
import { CollectionCard } from "./CollectionCard";
import { getMoreIdeasProducts } from "./moreIdeas";
import { MoreIdeasFab } from "./MoreIdeasFab";
import { MoreIdeasSection } from "./MoreIdeasSection";
import {
  CART_ROUTE,
  HOME_ROUTE,
  ALL_COLLECTIONS_ROUTE,
  collectionDetailRoute,
} from "./routes";
import {
  ALL_FAVORITES_COLLECTION_ID,
  setCollections,
  useCollections,
  useProductTokensByCollection,
} from "./savedStore";
import { SavedProductsSideCart } from "./SavedProductsSideCart";
import { SavedProductTile } from "./SavedProductTile";
import { useAccountDropdown } from "./useAccountDropdown";
import { useBrandCart } from "./useBrandCart";
import {
  SAVE_FLOW_POPOVER,
  useFavoritesController,
} from "./useFavoritesController";
import { type SavedViewMode, ViewModeToggle } from "./ViewModeToggle";


type SavedFilterKey = "on-sale" | "back-in-stock" | "low-stock";
// LiveToolbar: what leads the saved-products controls row.
const CONTROLS_FILTERS = 1;
const CONTROLS_TITLE = 2;

// Collections wrap to at most this many rows before the last cell becomes
// the "All collections" link to the full list.
const COLLAPSED_COLLECTION_ROWS = 2;

const SAVED_FILTERS: { key: SavedFilterKey; label: string }[] = [
  { key: "on-sale", label: "On sale" },
  { key: "back-in-stock", label: "Back in stock" },
  { key: "low-stock", label: "Low stock" },
];

/**
 * Saved products page (Figma: "All saved products"). A carousel of the
 * retailer's collections, filters and search over everything they've saved,
 * the saved product grid, and a "More ideas you might like" feed underneath.
 */
export default function SavedProductsCollectionsPage() {
  return (
    <CompassProvider>
      <SavedProductsPageInner />
    </CompassProvider>
  );
}

const SavedProductsPageInner = () => {
  const history = useHistory();
  const favorites = useFavoritesController({
    saveFlowVariant: SAVE_FLOW_POPOVER,
  });
  const accountDropdown = useAccountDropdown();
  const collectionColumns = useCollectionColumnCount();
  const collections = useCollections();

  const collapsedCellCount = COLLAPSED_COLLECTION_ROWS * collectionColumns;
  const showAllCollectionsTile = collections.length > collapsedCellCount;
  const visibleCollections = showAllCollectionsTile
    ? collections.slice(0, collapsedCellCount - 1)
    : collections;
  const productTokensByCollection = useProductTokensByCollection();
  const [appliedFilters, setAppliedFilters] = useState<Set<SavedFilterKey>>(
    new Set()
  );
  const [viewMode, setViewMode] = useState<SavedViewMode>("products");
  const [controlsVariant, setControlsVariant] =
    useState<number>(CONTROLS_FILTERS);
  const showsFilters = controlsVariant === CONTROLS_FILTERS;
  const [cartActionVariant, setCartActionVariant] =
    useState<number>(CART_ACTION_PILL);
  const cartAction = cartActionForVariant(cartActionVariant);
  const [brandFramingVariant, setBrandFramingVariant] = useState<number>(
    BRAND_FRAMING_DIVIDERS
  );
  const brandFraming = brandFramingForVariant(brandFramingVariant);

  const pageTitle = usePlaygroundLocalMsg("Saved products");
  const filtersLabel = usePlaygroundLocalMsg("Filters");
  const newCollectionName = usePlaygroundLocalMsg("New collection");

  useEffect(() => {
    document.title = `Saved Products & Cart — ${pageTitle}`;
  }, [pageTitle]);

  const savedTokens =
    productTokensByCollection[ALL_FAVORITES_COLLECTION_ID] ?? [];
  const savedProducts = savedTokens
    .map((productToken) => getEntityByToken(productToken, "product"))
    .filter((product): product is FaireProductEntity => Boolean(product));

  const cart = useBrandCart(savedProducts, {
    openOnQuantityChange: viewMode === "brands",
  });

  // The side cart belongs to the brand view; leaving it closes the cart.
  const handleViewModeChange = (nextViewMode: SavedViewMode) => {
    setViewMode(nextViewMode);
    if (nextViewMode === "products") {
      cart.closeSideCart();
    }
  };

  const filterCounts = countProductsByFilter(savedProducts);

  const activeFilters = showsFilters ? [...appliedFilters] : [];
  const visibleProducts = savedProducts.filter((product) =>
    activeFilters.every((filterKey) => matchesSavedFilter(product, filterKey))
  );

  const moreIdeas = getMoreIdeasProducts(new Set(savedTokens));

  const toggleFilter = (filterKey: SavedFilterKey) =>
    setAppliedFilters((current) => {
      const next = new Set(current);
      if (next.has(filterKey)) {
        next.delete(filterKey);
      } else {
        next.add(filterKey);
      }
      return next;
    });

  const handleNewCollection = () => {
    const customCollectionCount = collections.length;
    setCollections((current) => [
      ...current,
      {
        id: `collection-${Date.now()}`,
        name: `${newCollectionName} ${customCollectionCount}`,
      },
    ]);
  };

  const renderSavedTile = (product: FaireProductEntity) => (
    <SavedProductTile
      key={product.token}
      product={product}
      favorites={favorites}
      cart={cart}
    />
  );

  return (
    <>
      <LiveToolbar
        variantGroups={[
          {
            title: "Controls row",
            queryKey: "controls",
            variants: [
              { variantNumber: CONTROLS_FILTERS, label: "Filters" },
              { variantNumber: CONTROLS_TITLE, label: "All saved products title" },
            ],
            active: controlsVariant,
            onChange: setControlsVariant,
          },
          cartActionVariantGroup(cartActionVariant, setCartActionVariant),
          brandFramingVariantGroup(brandFramingVariant, setBrandFramingVariant),
        ]}
      />
      <RetailerLayout
        logoLinkUrl={HOME_ROUTE}
        cartHref={CART_ROUTE}
        onCartClick={() => history.push(CART_ROUTE)}
        cartCount={favorites.savedCount}
        accountDropdown={accountDropdown}
      >
        <div className="flex w-full items-start">
        <div className={pageStyle.cls()}>
          {/* Collections carousel */}
          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h1 className="type-fs-page-heading text-fs-text-primary">
                <PlaygroundLocalMsg defaultMessage="Saved products" />
              </h1>
              <Button
                variant="tertiary"
                size="small"
                iconProps={{ Component: Add, position: "start" }}
                onClick={handleNewCollection}
              >
                <PlaygroundLocalMsg defaultMessage="New collection" />
              </Button>
            </div>
            <div className={collectionGridStyle.cls()}>
              {visibleCollections.map((collection) => (
                <CollectionCard
                  key={collection.id}
                  collection={collection}
                  productTokensByCollection={productTokensByCollection}
                  fluid
                  label={
                    collection.id === ALL_FAVORITES_COLLECTION_ID
                      ? pageTitle
                      : undefined
                  }
                  onClick={() =>
                    history.push(collectionDetailRoute(collection.id))
                  }
                />
              ))}
              {showAllCollectionsTile ? (
                <AllCollectionsTile
                  onClick={() => history.push(ALL_COLLECTIONS_ROUTE)}
                />
              ) : null}
            </div>
          </section>

          {/* All saved products */}
          <section className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {showsFilters ? (
                <div className="flex items-center gap-2">
                  <FilterButton label={filtersLabel} Icon={Filter} />
                  {SAVED_FILTERS.map((filter) => (
                    <CountFilterToggle
                      key={filter.key}
                      label={filter.label}
                      count={filterCounts[filter.key]}
                      applied={appliedFilters.has(filter.key)}
                      onClick={() => toggleFilter(filter.key)}
                    />
                  ))}
                </div>
              ) : (
                <h2 className="type-fs-page-heading text-fs-text-primary">
                  <PlaygroundLocalMsg defaultMessage="All saved products" />
                </h2>
              )}
              <ViewModeToggle
                value={viewMode}
                onChange={handleViewModeChange}
              />
            </div>

            {visibleProducts.length === 0 ? (
              <div className="flex justify-center py-16">
                <p className="type-fs-paragraph text-fs-text-subdued">
                  {savedProducts.length === 0 ? (
                    <PlaygroundLocalMsg defaultMessage="Products you save will appear here." />
                  ) : (
                    <PlaygroundLocalMsg defaultMessage="No saved products match these filters." />
                  )}
                </p>
              </div>
            ) : viewMode === "brands" ? (
              <BrandGroupedProducts
                products={visibleProducts}
                cart={cart}
                cartAction={cartAction}
                framing={brandFraming}
                renderTile={renderSavedTile}
              />
            ) : (
              <div
                className={savedProductGridStyle.cls({
                  compact: cart.isSideCartOpen,
                })}
              >
                {visibleProducts.map(renderSavedTile)}
              </div>
            )}
          </section>

          <MoreIdeasSection
            products={moreIdeas}
            renderTile={renderSavedTile}
            compact={cart.isSideCartOpen}
          />
        </div>
        {cart.sideCartBrand ? (
          <SavedProductsSideCart
            open={cart.isSideCartOpen}
            brandName={cart.sideCartBrand.name}
            minimum={cart.sideCartBrand.minimum}
            items={cart.sideCartItems}
            onChangeQuantity={cart.setCartQuantity}
            onRemove={(productToken) => cart.setCartQuantity(productToken, 0)}
            onViewCart={() => history.push(CART_ROUTE)}
            onClose={cart.closeSideCart}
          />
        ) : null}
        </div>
      </RetailerLayout>
      <MoreIdeasFab
        imageUrls={moreIdeas.slice(0, 2).map((product) => product.imageUrl)}
      />
      {favorites.popoverElement}
      {favorites.toastElement}
      {favorites.collectionToastElement}
    </>
  );
};

// --- Filter toggles ---------------------------------------------------------

interface CountFilterToggleProps {
  label: string;
  count: number;
  applied: boolean;
  onClick: () => void;
}

/**
 * Slate's FilterToggle only accepts a string label, so this reproduces its
 * pill styling locally to fit the numeric count badge the design places inside
 * the toggle.
 */
const CountFilterToggle = ({
  label,
  count,
  applied,
  onClick,
}: CountFilterToggleProps) => (
  <button
    type="button"
    role="switch"
    aria-checked={applied}
    onClick={onClick}
    className={countFilterToggleStyle.cls({ applied })}
  >
    <span className="type-fs-paragraph">{label}</span>
    <span className={countBadgeStyle.cls({ applied })}>{count}</span>
  </button>
);

// --- Collections grid -------------------------------------------------------

/** Cards per row; mirrors `collectionGridStyle`'s breakpoints. */
const useCollectionColumnCount = (): number => {
  const [columns, setColumns] = useState(() => collectionColumnsForWidth());

  useEffect(() => {
    const updateColumns = () => setColumns(collectionColumnsForWidth());
    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  return columns;
};

const collectionColumnsForWidth = (): number => {
  if (typeof window === "undefined") {
    return 5;
  }
  const width = window.innerWidth;
  if (width >= 1024) {
    return 5;
  }
  return width >= 768 ? 3 : 2;
};

/** Last grid cell when collections overflow (Figma: "Product Tile/Shop All"). */
const AllCollectionsTile = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={allCollectionsTileStyle.cls()}
  >
    <span className={allCollectionsCircleStyle.cls()}>
      <ArrowRight width="32" height="32" />
    </span>
    <span className="type-fs-paragraph-medium text-fs-text-primary">
      <PlaygroundLocalMsg defaultMessage="All collections" />
    </span>
  </button>
);

// --- Data helpers -----------------------------------------------------------

/** Stable per-product bucket so the prototype's filter chips have data behind them. */
const productBucket = (product: FaireProductEntity): number => {
  let hash = 0;
  for (let index = 0; index < product.token.length; index++) {
    hash = (hash * 31 + product.token.charCodeAt(index)) | 0;
  }
  return Math.abs(hash) % 7;
};

const matchesSavedFilter = (
  product: FaireProductEntity,
  filterKey: SavedFilterKey
): boolean => {
  const bucket = productBucket(product);
  switch (filterKey) {
    case "on-sale":
      return bucket === 0;
    case "back-in-stock":
      return bucket === 1 || bucket === 2;
    case "low-stock":
      return bucket === 3 || bucket === 4;
  }
};

const countProductsByFilter = (
  products: FaireProductEntity[]
): Record<SavedFilterKey, number> => ({
  "on-sale": products.filter((product) => matchesSavedFilter(product, "on-sale"))
    .length,
  "back-in-stock": products.filter((product) =>
    matchesSavedFilter(product, "back-in-stock")
  ).length,
  "low-stock": products.filter((product) =>
    matchesSavedFilter(product, "low-stock")
  ).length,
});

// --- Styles -----------------------------------------------------------------

const pageStyle = tsv({
  base: [
    "mx-auto flex w-full min-w-0 max-w-[1920px] flex-1 flex-col gap-8",
    "px-6 pt-8 pb-16 lg:px-12",
  ],
});

const collectionGridStyle = tsv({
  base: ["grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5"],
});

const allCollectionsTileStyle = tsv({
  base: [
    "flex aspect-[3/2] w-full flex-col items-center justify-center gap-4",
    "transition-opacity hover:opacity-80",
  ],
});

const allCollectionsCircleStyle = tsv({
  base: [
    "bg-fs-surface-tertiary text-fs-text-primary",
    "flex size-14 items-center justify-center rounded-full",
  ],
});

const countFilterToggleStyle = tsv({
  base: [
    "flex h-10 items-center gap-2 rounded-full border px-4",
    "transition-colors",
  ],
  variants: {
    applied: {
      true: "bg-fs-surface-inverse border-fs-surface-inverse text-fs-text-on-dark",
      false:
        "bg-fs-action-surface-inverse border-fs-action-border-subdued text-fs-text-primary hover:border-fs-action-border-hover",
    },
  },
});

const countBadgeStyle = tsv({
  base: [
    "flex h-4 min-w-4 items-center justify-center rounded-full px-[5px]",
    "text-[10px] leading-none font-medium tracking-[0.15px]",
  ],
  variants: {
    applied: {
      true: "bg-fs-surface-primary text-fs-text-primary",
      false: "bg-fs-surface-tertiary text-fs-text-primary",
    },
  },
});

